import { Canvas } from "@react-three/fiber";
import { Leva } from "leva";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import AboutPacketPanic from "./components/AboutPacketPanic/AboutPacketPanic.tsx";
import EndScreen from "./components/EndScreen/EndScreen.tsx";
import Explanation from "./components/Explanation/Explanation.tsx";
import GameOverScreen from "./components/GameOverScreen/GameOverScreen.tsx";
import Interface from "./components/Interface/Interface.tsx";
import IntroScreen from "./components/IntroScreen/IntroScreen.tsx";
import LevelPicker from "./components/LevelPicker/LevelPicker.tsx";
import MuteButton from "./components/MuteButton/MuteButton.tsx";
import PauseButton from "./components/PauseButton/PauseButton.tsx";
import PauseScreen from "./components/PauseScreen/PauseScreen.tsx";
import ReadyScreen from "./components/ReadyScreen/ReadyScreen.tsx";
import TutorialScreen from "./components/TutorialScreen/TutorialScreen.tsx";
import Experience from "./Experience.js";
import GarageTransition from "./GarageTransition.tsx";
import useGame from "./stores/useGame.js";
import "./style.css";
import "./utils/i18n";

import { init } from "@plausible-analytics/tracker";
import LanguageSwitcher from "./components/LanguageSwitcher/LanguageSwitcher.tsx";

function GameApp() {
  const phase = useGame((state) => state.phase);
  const isPaused = useGame((state) => state.isPaused);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Monitor transition stat
  useEffect(() => {
    const checkTransition = () => {
      setIsTransitioning(!!window.isTransitioning);
    };

    checkTransition();
    const interval = setInterval(checkTransition, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const preventGesture = (e) => e.preventDefault();

    document.addEventListener("gesturestart", preventGesture, {
      passive: false,
    });
    document.addEventListener("gesturechange", preventGesture, {
      passive: false,
    });
    document.addEventListener("gestureend", preventGesture, { passive: false });

    return () => {
      document.removeEventListener("gesturestart", preventGesture);
      document.removeEventListener("gesturechange", preventGesture);
      document.removeEventListener("gestureend", preventGesture);
    };
  }, []);

  return (
    <>
      {(phase == "intro" || isPaused) && (
        <div className={`language-switcher inset`}>
          <LanguageSwitcher />
        </div>
      )}
      <div
        className={`top-right-buttons ${
          isPaused ||
          phase === "intro" ||
          phase === "gameFinished" ||
          phase === "about"
            ? "inset"
            : ""
        }`}
      >
        {phase === "playing" && !isTransitioning && <PauseButton />}
        <MuteButton />
      </div>
      <Leva collapsed hidden />
      <GarageTransition />
      {phase === "intro" ? (
        <IntroScreen />
      ) : phase === "gameFinished" ? (
        <EndScreen />
      ) : (
        <>
          {phase === "ready" && <ReadyScreen />}
          {phase === "ended" && <GameOverScreen />}
          {phase === "explanation" && <Explanation />}
          {phase === "tutorial" && <TutorialScreen />}
          {phase === "levelPicker" && <LevelPicker />}
          {phase === "about" && <AboutPacketPanic />}
          {isPaused && <PauseScreen />}
          <Canvas
            shadows
            camera={{
              fov: 45,
              near: 0.1,
              far: 200,
              position: [15, 40, 10],
            }}
          >
            <Experience />
          </Canvas>
          {!isPaused && (
            <>
              <Interface />
            </>
          )}
        </>
      )}
    </>
  );
}

// Standalone EndScreen component for the route
function StandaloneEndScreen() {
  const phase = useGame((state) => state.phase);

  return (
    <>
      <div
        className={`top-right-buttons ${
          phase === "intro" || phase === "gameFinished" || phase === "about"
            ? "inset"
            : ""
        }`}
      >
        <MuteButton />
      </div>
      <EndScreen />
    </>
  );
}

function App() {
  init({
    domain: "packet-panic.nl",
  });
  const initializeTabVisibility = useGame(
    (state) => state.initializeTabVisibility
  );
  const cleanupRef = useRef<(() => void) | null>(null);

  // Initialize tab visibility handling once for the entire app
  useEffect(() => {
    cleanupRef.current = initializeTabVisibility();

    // Cleanup on unmount
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [initializeTabVisibility]);

  return (
    <Router>
      <Routes>
        {/* Main game route */}
        <Route path="/" element={<GameApp />} />

        {/* Standalone endscreen route */}
        <Route path="/endscreen" element={<StandaloneEndScreen />} />

        {/* Redirect any unknown routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

const container = document.querySelector("#root");
if (!container) {
  throw new Error('Root container "#root" not found');
}
const root = ReactDOM.createRoot(container);
root.render(<App />);
